import React, { Component, type ErrorInfo, type ReactNode } from "react";
import { XO_GOLD, XO_PAPER } from "./xoConvergenceBrand";
import { performPublicRouteHardRefresh } from "../lib/xo_public_route_recovery";
import { XOPublicRecoveryNavLinks } from "./XOPublicRecoveryNav";
import { PUBLIC_SURFACE_FALLBACK } from "../lib/continuityTraversalCanon";

type Props = { children: ReactNode };

type State = {
  hasError: boolean;
  chunkLikely: boolean;
};

function isLikelyChunkOrImportError(error: unknown): boolean {
  const e = error instanceof Error ? error : new Error(String(error));
  const blob = `${e.name} ${e.message}`.toLowerCase();
  return (
    blob.includes("chunkloaderror") ||
    blob.includes("loading chunk") ||
    blob.includes("loading css chunk") ||
    blob.includes("failed to fetch dynamically imported module") ||
    blob.includes("error loading dynamically imported module") ||
    blob.includes("importing a module script failed") ||
    blob.includes("dynamically imported module")
  );
}

function PublicRouteErrorFallback({ chunkLikely }: { chunkLikely: boolean }) {
  return (
    <div
      role="alert"
      style={{
        maxWidth: 520,
        marginInline: "auto",
        padding: "48px 24px 64px",
        boxSizing: "border-box",
        textAlign: "center",
      }}
    >
      <p
        style={{
          margin: 0,
          fontSize: "0.6875rem",
          fontWeight: 500,
          letterSpacing: "0.12em",
          textTransform: "uppercase",
          color: "rgba(200, 164, 77, 0.72)",
        }}
      >
        XO
      </p>
      <h1
        style={{
          margin: "16px 0 0",
          fontSize: "clamp(1.25rem, 3vw, 1.5rem)",
          fontWeight: 600,
          letterSpacing: "0.04em",
          lineHeight: 1.35,
          color: XO_PAPER,
        }}
      >
        {PUBLIC_SURFACE_FALLBACK.title}
      </h1>
      <p
        style={{
          margin: "16px 0 0",
          fontSize: "0.9375rem",
          fontWeight: 400,
          lineHeight: 1.5,
          color: "rgba(246, 240, 230, 0.72)",
        }}
      >
        {chunkLikely ? PUBLIC_SURFACE_FALLBACK.chunk : PUBLIC_SURFACE_FALLBACK.body}
      </p>
      <p style={{ margin: "24px 0 0" }}>
        <XOPublicRecoveryNavLinks />
      </p>
      <button
        type="button"
        onClick={() => {
          performPublicRouteHardRefresh();
        }}
        style={{
          marginTop: 28,
          padding: "10px 20px",
          borderRadius: 999,
          border: "1px solid rgba(200, 164, 77, 0.22)",
          background: "rgba(200, 164, 77, 0.08)",
          color: XO_GOLD,
          fontWeight: 500,
          fontSize: "0.8125rem",
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          cursor: "pointer",
        }}
      >
        Refresh page
      </button>
    </div>
  );
}

/**
 * Catches render errors in public route trees so the ink shell never reads as an empty failure.
 */
export class XOPublicRouteErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, chunkLikely: false };

  static getDerivedStateFromError(error: unknown): Partial<State> {
    return { hasError: true, chunkLikely: isLikelyChunkOrImportError(error) };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo): void {
    if (import.meta.env.DEV) {
      const pathname =
        typeof window !== "undefined" && typeof window.location?.pathname === "string"
          ? window.location.pathname
          : "(unknown)";
      console.error("[XO public route failure]", { pathname, error, errorInfo: info, componentStack: info.componentStack });
    }
  }

  override render(): ReactNode {
    if (this.state.hasError) {
      return <PublicRouteErrorFallback chunkLikely={this.state.chunkLikely} />;
    }
    return this.props.children;
  }
}
